import React, { useState, useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Search, School, Download, Loader2, Layers, Calendar, Eye, Gift, ArrowDownCircle, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

const ITEMS_PER_PAGE = 6;

const PastQuestionSkeleton = () => (
  <div className="bg-white rounded-xl border border-cream-200 p-4 shadow-sm flex flex-col justify-between animate-pulse">
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="w-16 h-4 bg-cream-200 rounded-md" />
        <div className="w-24 h-3 bg-cream-200 rounded" />
      </div>
      <div className="w-24 h-5 bg-cream-300 rounded mb-2" />
      <div className="w-full h-3 bg-cream-100 rounded mb-1" />
      <div className="w-2/3 h-3 bg-cream-100 rounded mb-4" />
      <div className="w-1/2 h-3 bg-cream-200 rounded" />
    </div>
    <div className="grid grid-cols-2 gap-2 mt-5">
      <div className="h-8 bg-cream-200 rounded-lg" />
      <div className="h-8 bg-cream-200 rounded-lg" />
    </div>
  </div>
);

export const PastQuestions = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSchool, setSelectedSchool] = useState('');
  const [institutionType, setInstitutionType] = useState('university'); 
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedSemester, setSelectedSemester] = useState('');

  const [activeFilters, setActiveFilters] = useState({
    search: '',
    school: '',
    level: '',
    semester: ''
  });

  // Lightbox Modal states
  const [activeQuestion, setActiveQuestion] = useState(null); 
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  useEffect(() => {
    setSelectedLevel('');
  }, [institutionType]);

  const {
    data,
    isLoading: loading,
    isFetchingNextPage: loadingMore,
    hasNextPage: hasMore,
    fetchNextPage: handleLoadMore,
    error
  } = useInfiniteQuery({
    queryKey: ['pastQuestions', activeFilters],
    queryFn: async ({ pageParam = 0 }) => {
      let query = supabase.from('contributions').select('*', { count: 'exact' }).eq('status', 'approved');

      if (activeFilters.search) {
        query = query.or(`department.ilike.%${activeFilters.search}%,course_code.ilike.%${activeFilters.search}%,course_title.ilike.%${activeFilters.search}%`);
      }
      if (activeFilters.school) {
        query = query.ilike('school', `%${activeFilters.school}%`);
      }
      if (activeFilters.level) {
        query = query.eq('level', activeFilters.level);
      }
      if (activeFilters.semester) {
        query = query.eq('semester', activeFilters.semester);
      }

      const from = pageParam * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      const { data: dbData, error: dbError, count } = await query
        .order('year', { ascending: false })
        .range(from, to);

      if (dbError) throw dbError;

      return {
        freshRecords: dbData || [],
        totalCount: count || 0,
        nextPage: pageParam + 1
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const currentTotalLoaded = allPages.reduce((sum, current) => sum + current.freshRecords.length, 0);
      return currentTotalLoaded < lastPage.totalCount ? lastPage.nextPage : undefined;
    },
    staleTime: 1000 * 60 * 10,
  });

  const questions = data ? data.pages.flatMap(page => page.freshRecords) : [];

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    setActiveFilters({
      search: searchQuery,
      school: selectedSchool,
      level: selectedLevel,
      semester: selectedSemester
    });
  };

  const handleDownloadFile = (fileUrl, courseCode) => {
    if (!fileUrl) return toast.error('No document file link available');
    const linksToDownload = Array.isArray(fileUrl) ? fileUrl : [fileUrl];
    
    linksToDownload.forEach((url, idx) => {
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.setAttribute('download', `${courseCode}_page_${idx + 1}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  // Convert URLs cleanly to a continuous iterable array
  const filesArray = activeQuestion 
    ? Array.isArray(activeQuestion.file_url) 
      ? activeQuestion.file_url 
      : [activeQuestion.file_url]
    : [];

  // Tracks active index by monitoring horizontal scroll distance benchmarks
  const handleScrollUpdate = (e) => {
    const { scrollLeft, clientWidth } = e.currentTarget;
    const newIndex = Math.round(scrollLeft / clientWidth);
    if (newIndex !== currentPageIndex) {
      setCurrentPageIndex(newIndex);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-cream-100 to-brown-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        
        <div className="mb-6 sm:mb-8 text-center sm:text-left">
          <h1 className="text-2xl sm:text-3xl font-bold text-brown-900 tracking-tight">Free Past Questions</h1>
          <p className="text-xs sm:text-base text-brown-600 mt-1">Search for past questions across major Nigerian universities and polytechnics completely free.</p>
        </div>

        {/* Filter Engine Bar */}
        <form onSubmit={handleSearchSubmit} className="flex flex-col gap-4 bg-white p-4 rounded-2xl border border-cream-300 shadow-sm mb-6 sm:mb-8">
          <div className="relative flex items-center border-b border-cream-200 pb-2">
            <Search className="absolute left-2 w-4 h-4 text-brown-400" />
            <input 
              type="text" 
              placeholder="Search department, course title or course code (e.g. MEC 111, GNS 201)..."
              className="w-full pl-8 pr-3 py-1.5 text-sm focus:outline-none text-brown-900 bg-transparent"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 items-center">
            <div className="flex items-center bg-cream-50/50 rounded-xl px-3 py-1.5 border border-cream-200">
              <School className="w-4 h-4 text-brown-400 shrink-0 mr-2" />
              <input 
                type="text"
                placeholder="School (e.g. YABATECH)"
                className="w-full text-xs sm:text-sm focus:outline-none text-brown-900 bg-transparent"
                value={selectedSchool}
                onChange={e => setSelectedSchool(e.target.value)}
              />
            </div>

            <div className="flex items-center bg-cream-50/50 rounded-xl px-3 py-1.5 border border-cream-200">
              <Layers className="w-4 h-4 text-brown-400 shrink-0 mr-2" />
              <select
                className="w-full text-xs sm:text-sm bg-transparent focus:outline-none text-brown-700 font-medium cursor-pointer"
                value={institutionType}
                onChange={e => setInstitutionType(e.target.value)}
              >
                <option value="university">University</option>
                <option value="polytechnic">Polytechnic</option>
              </select>
            </div>

            <div className="flex items-center bg-cream-50/50 rounded-xl px-3 py-1.5 border border-cream-200">
              <select 
                className="w-full text-xs sm:text-sm bg-transparent focus:outline-none text-brown-700 font-medium cursor-pointer"
                value={selectedLevel}
                onChange={e => setSelectedLevel(e.target.value)}
              >
                <option value="">All Levels</option>
                {institutionType === 'university' ? (
                  <>
                    <option value="100">100 Lvl</option>
                    <option value="200">200 Lvl</option>
                    <option value="300">300 Lvl</option>
                    <option value="400">400 Lvl</option>
                    <option value="500">500 Lvl</option>
                  </>
                ) : (
                  <>
                    <option value="ND 1">ND 1</option>
                    <option value="ND 2">ND 2</option>
                    <option value="HND 1">HND 1</option>
                    <option value="HND 2">HND 2</option>
                  </>
                )}
              </select>
            </div>

            <div className="flex items-center bg-cream-50/50 rounded-xl px-3 py-1.5 border border-cream-200">
              <Calendar className="w-4 h-4 text-brown-400 shrink-0 mr-2" />
              <select 
                className="w-full text-xs sm:text-sm bg-transparent focus:outline-none text-brown-700 font-medium cursor-pointer"
                value={selectedSemester}
                onChange={e => setSelectedSemester(e.target.value)}
              >
                <option value="">All Semesters</option>
                <option value="1">1st Semester</option>
                <option value="2">2nd Semester</option>
              </select>
            </div>

            <button 
              type="submit"
              className="w-full py-2 bg-brown-600 hover:bg-brown-700 text-white font-semibold text-xs sm:text-sm rounded-xl transition-colors shadow-sm"
            >
              Apply Filter
            </button>
          </div>
        </form>

        {/* Query Rendering Grid Layer */}
        {error ? (
          <div className="text-center py-12 px-6 bg-red-50 rounded-2xl border border-red-200 shadow-sm max-w-2xl mx-auto flex flex-col items-center">
            <div className="p-3 bg-red-100 rounded-full mb-4 text-red-600">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-red-900 tracking-tight">Failed to load past questions</h3>
            <p className="text-sm text-red-600 mt-1 leading-relaxed max-w-md">
              {error.message || 'A network error occurred connecting to the database server. Please check your connection or refresh.'}
            </p>
          </div>
        ) : loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => <PastQuestionSkeleton key={i} />)}
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-12 px-6 bg-white rounded-2xl border border-cream-300 shadow-sm max-w-2xl mx-auto flex flex-col items-center animate-fade-in">
            <div className="p-3.5 bg-amber-50 rounded-full mb-4 text-amber-600 border border-amber-100">
              <Gift className="w-8 h-8 animate-bounce" />
            </div>
            <h3 className="text-lg font-bold text-brown-900 tracking-tight">Oops! We are yet to get these past questions.</h3>
            <p className="text-sm text-brown-600 max-w-md mt-2 leading-relaxed">
              But you could also help us source them and get rewarded with <span className="font-bold text-brown-950 underline decoration-yellow-500 decoration-2">a week of premium access</span> once verified!
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-fade-in">
              {questions.map((item) => (
                <div key={item.id} className="bg-white rounded-xl border border-cream-200 p-4 shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="px-2 py-0.5 text-[10px] font-bold bg-brown-100 text-brown-800 rounded-md uppercase">{item.school}</span>
                      <span className="text-xs text-brown-500 font-medium">{item.year} • {item.semester === '1' || item.semester === 1 ? '1st' : '2nd'} Semester</span>
                    </div>
                    <h3 className="text-base font-bold text-brown-900">{item.course_code}</h3>
                    {item.course_title && <p className="text-xs text-brown-600 mt-0.5 line-clamp-1">{item.course_title}</p>}
                    <p className="text-xs text-brown-500 mt-2 font-medium">Dept: <span className="text-brown-700">{item.department}</span> • <span className="text-brown-700 font-bold">{item.level}</span></p>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <button 
                      type="button"
                      onClick={() => {
                        setActiveQuestion(item);
                        setCurrentPageIndex(0);
                      }}
                      className="flex items-center justify-center space-x-1.5 py-2 bg-brown-600 hover:bg-brown-700 text-white font-medium text-xs rounded-lg transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      <span>View Paper</span>
                    </button>

                    <button 
                      type="button"
                      onClick={() => handleDownloadFile(item.file_url, item.course_code)}
                      className="flex items-center justify-center space-x-1.5 py-2 bg-cream-100 hover:bg-cream-200 text-brown-900 font-medium text-xs rounded-lg transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" />
                      <span>Download</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {hasMore && (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => handleLoadMore()}
                  disabled={loadingMore}
                  className="flex items-center space-x-2 px-6 py-2.5 bg-white border border-cream-300 text-brown-700 hover:bg-cream-50 font-semibold text-sm rounded-xl transition-all shadow-sm disabled:opacity-50"
                >
                  {loadingMore ? (
                    <Loader2 className="w-4 h-4 animate-spin text-brown-600" />
                  ) : (
                    <ArrowDownCircle className="w-4 h-4 text-brown-600" />
                  )}
                  <span>{loadingMore ? 'Loading matching papers...' : 'Load More Questions'}</span>
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* SWIPE SLIDE PREVIEW MODAL */}
      {activeQuestion && (
        <div 
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/95 p-4 backdrop-blur-sm"
          onClick={() => setActiveQuestion(null)}
        >
          <div className="w-full max-w-4xl flex items-center justify-between text-white mb-4 px-2">
            <div className="text-sm font-medium flex items-center gap-2">
              <span>{activeQuestion.course_code} Exam Sheet</span>
              {filesArray.length > 1 && (
                <span className="px-2 py-0.5 bg-white/10 rounded-full text-xs text-yellow-400">
                  Page {currentPageIndex + 1} of {filesArray.length}
                </span>
              )}
            </div>
            <button 
              type="button"
              className="text-white/80 hover:text-white font-bold text-xs bg-white/10 px-3 py-1.5 rounded-lg"
              onClick={() => setActiveQuestion(null)}
            >
              ✕ Close
            </button>
          </div>

          {/* Swipe/Scroll Container Window */}
          <div className="relative max-w-4xl w-full h-[75vh] flex items-center justify-center bg-zinc-900/40 rounded-2xl border border-white/5 overflow-hidden">
            
            <div 
              className="w-full h-full flex overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar"
              onScroll={handleScrollUpdate}
              onClick={(e) => e.stopPropagation()}
            >
              {filesArray.map((url, idx) => {
                const isPdf = url?.toLowerCase().split('?')[0].endsWith('.pdf');
                
                return (
                  <div key={idx} className="w-full h-full flex-shrink-0 snap-center flex items-center justify-center p-2">
                    {isPdf ? (
                      <iframe 
                        src={url} 
                        className="w-full h-full bg-white rounded-lg border-0" 
                        title={`PDF View Frame - Page ${idx + 1}`}
                      />
                    ) : (
                      <img 
                        src={url} 
                        alt={`Exam Material Page ${idx + 1}`} 
                        className="max-w-full max-h-full object-contain rounded-lg shadow-2xl select-none"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pagination Navigation Dots indicator */}
          {filesArray.length > 1 && (
            <div className="flex space-x-2 mt-4" onClick={(e) => e.stopPropagation()}>
              {filesArray.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === currentPageIndex ? 'bg-yellow-400 scale-125 w-4' : 'bg-white/30'}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PastQuestions;